<template>
    <div>
        <div class="advanced" :class="showAdvanced ? 'show' : 'hide'">
            <div class="row">
                <div class="col-md-4" v-if="!isStudent || !lockUser">
                    <!-- Filter by Student -->
                    <div class="form-group">
                        <label>{{ $t("student") }}</label>
                        <input
                            type="text"
                            v-model="innerValue.student_displayname"
                            class="form-control"
                            @focus="selectStudent"
                        />
                    </div>
                </div>
                <div v-if="isStudent || !lockUser" class="col-md-4">
                    <!-- Filter by Teacher -->
                    <div class="form-group">
                        <label>{{ $t("teacher") }}</label>
                        <input
                            type="text"
                            v-model="innerValue.teacher_displayname"
                            class="form-control"
                            @focus="selectTeacher"
                        />
                    </div>
                </div>
                <div class="col-md-4">
                    <!-- Filter by Clinic -->
                    <SelectInput 
                        :value="innerValue.clinicId" 
                        @input="id => innerValue.clinicId = id ? id : null"
                        :label="$t('clinic')" 
                        :options="$config.getValue('clinics')"
                        optionLabel="name"
                        optionValue="id"
                    />
                </div>
                <div class="col-md-4">
                    <!-- Filter by Programme -->
                    <SelectInput
                        :value="innerValue.programme ? innerValue.programme.Id : null"
                        @input="id => innerValue.programme = id ? Object.values($config.getValue('programmes')).filter(el => el.Id === id)[0] : null"
                        :label="$t('programme')"
                        :options="$config.getValue('programmes')"
                        optionLabel="Name"
                        optionValue="Id"
                        :nullLabel="$t('any')"
                    />
                </div>
                <div class="col-md-4">
                    <!-- Filter by Year -->
                    <SelectInput 
                        v-model="innerValue.year"
                        :label="$t('year')"
                        :options="innerValue.programme ? $config.getProgrammeYears(innerValue.programme.Id) : []"
                        :labelCb="year => $config.getYearStr(year, innerValue.programme.Id)"
                        :nullLabel="$t('any')"
                    />
                </div>
                <div class="col-md-4">
                    <!-- Filter by Group -->
                    <SelectInput 
                        v-model="innerValue.group"
                        :label="$t('group')"
                        :options="$config.getValue('groups')"
                        :indexIsValue="true"
                        :nullLabel="$t('any')"
                    />
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-md-3">
                    <!-- Locked? -->
                    <SelectInput 
                        v-model="innerValue.isLocked"
                        :label="$t('locked')"
                        :options="{
                            'true': $t('locked'),
                            'false': $t('unlocked'),
                        }"
                        :indexIsValue="true"
                    />
                </div>
                <div class="col-md-3">
                    <!-- Reflection? -->
                    <SelectInput 
                        v-model="innerValue.reflection"
                        :label="$t('reflection')"
                        :options="{
                            'true': $t('yes'),
                            'false': $t('no'),
                        }"
                        :indexIsValue="true"
                    />
                </div>
                <div class="col-md-3">
                    <!-- Filter by Specialty -->
                    <SelectInput 
                        v-model="innerValue.specialtyId"
                        :label="$t('specialty')"
                        :options="$config.getValue('specialties')"
                        optionLabel="name"
                        optionValue="id"
                    />
                </div>
                <div class="col-md-3">
                    <!-- Filter by Participation Type -->
                    <SelectInput
                        v-model="innerValue.typeId"
                        :label="$t('session-type')"
                        :options="$config.getValue('participationTypes')"
                        optionLabel="name"
                        optionValue="id"
                    />
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12 d-flex flex-row align-items-center justify-content-between">
                <SearchBar v-model="innerValue.search" placeholder="Search..." label="Search" />
                <button type="button" class="btn" :class="showAdvanced ? 'btn-primary' : 'btn-secondary'" @click="showAdvanced = !showAdvanced">{{ $t('advanced') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import SelectInput from "src/components/Inputs/SelectInput.vue"
import SearchBar from "src/components/Table/SearchBar.vue"

export default {
    components: {
        SelectInput,
        SearchBar,
    },
    props: {
        value: {
            type: Object,
            default: () => {
                return {
                    search: "",
                    isLocked: null,
                    reflection: null,
                    student: null,
                    student_displayname: "",
                    teacher: null,
                    teacher_displayname: "",
                    programme: null,
                    year: null,
                    group: null,
                    specialtyId: null,
                    typeId: null,
                    clinicId: null,
                }
            }
        },
        lockUser: Boolean   // If true, locks results to those owned by the user (as a student or as a teacher)
    },
    mounted() {
        this.setDefaults()
    },
    data() {
        return {
            showAdvanced: false,
            innerValue: this.value,
        }
    },
    methods: {
        setDefaults() {
            const newVal = {
				search: "",
				isLocked: null,
				reflection: null,
				student: null,
				student_displayname: "All",
				teacher: null,
				teacher_displayname: "All",
				programme: null,
				year: null,
				group: null,
				specialtyId: null,
				typeId: null,
				clinicId: null
			}

            this.setLockDefaults(newVal)

            this.$emit('input', newVal)
		},
        setLockDefaults(newVal) {
            if (this.lockUser) {
                if (this.isStudent) {
                    newVal.studentId = this.$UserSession.getValue('id')
                    newVal.student_displayname = this.$UserSession.getValue('displayName')
                }
                else if (this.isTeacher) {
                    newVal.teacherId = this.$UserSession.getValue('id')
                    newVal.teacher_displayname = this.$UserSession.getValue('displayName')
                }
            }
        },
        selectStudent() {
			const self = this;
			this.$users.getStudents.call(this).then((students) => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-a-student"),
					selectCallback: function (selection) {
						if (self.innerValue.studentId == selection.id) {
							self.innerValue.student_displayname = "";
							self.innerValue.studentId = null;
						} else {
							self.innerValue.student_displayname = selection.displayName;
							self.innerValue.studentId = selection.id;
						}
                        self.$emit('input', self.innerValue)
						self.$store.commit("closeModal");
					},
					selected: [
						{
							id: this.innerValue.studentId,
						},
					],
					data: [{
						"id": null ,
						"displayName": this.$t('all')
					}].concat(students),
					captionKey: "displayName",
                    subcaptionKey: "studentid",
					studentSearchFilters: true
				});
			});
		},
		selectTeacher() {
            const self = this;
            this.$users.getTeachers.call(this).then((teachers) => {
                const processedTeachers = teachers.map((teacher) => {
                if (teacher.status && teacher.status.toLowerCase() === "closed") {
                    teacher.subcaption = this.$t("(Closed)");
                }
                return teacher;
                });
                this.$store.commit("openModal", {
                type: "selector",
                title: this.$t("select-a-teacher"),
                selectCallback: function (selection) {
                    if (self.innerValue.teacherId == selection.id) {
                    self.innerValue.teacher_displayname = "";
                    self.innerValue.teacherId = null;
                    } else {
                    self.innerValue.teacher_displayname = selection.displayName;
                    self.innerValue.teacherId = selection.id;
                    }
                    self.$emit("input", self.innerValue);
                    self.$store.commit("closeModal");
                },
                selected: [
                    {
                    id: this.innerValue.teacherId,
                    },
                ],
                data: [
                    {
                    id: null,
                    displayName: this.$t("all"),
                    },
                ].concat(processedTeachers),
                captionKey: "displayName",
                subcaptionKey: "subcaption"
                });
            });
        },

    },
    computed: {
		isTeacher() {
			return this.$UserSession.hasPriv.call(this, "MARK_TREATMENTSESSIONS");
		},
        isStudent() {
			return this.$UserSession.hasPriv.call(this, "APP_STUDENT");
		},
	},
    watch: {
        innerValue: {
            deep: true,
            handler: function(val) {
				if (!val.programme) val.year = null
				this.$emit('input', val)
            }
        }
    }
}
</script>

<style lang="scss">
.advanced {
    transition: all 400ms ease-in-out;
}
.hide {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
}
.show {
    max-height: 1000px;
    opacity: 1;
}
</style>